<template>
  <div class="document-list" data-test="incorporation-table">
        <div class="table-top d-flex flex-wrap justify-content-between border-bottom">
            <div class="document-title d-flex justify-content-center align-items-center mb-2">
                <div class="title"> 
                    <h1> {{ documentTitle }} </h1> 
                </div>

                <div v-if="shouldShowYearOptions">
                    <b-form-select 
                        v-model="selectedFilter" 
                        class="d-flex ml-.5 h3" 
                        value-field="companyProcessId"
                        text-field="filter"
                        :options="filterByOptions" 
                        @change="handleFilterByOnChange"
                    />
                </div>
            </div>

            <div class="d-flex">
                <div v-if="documentList.length">
                    <div v-if="dataStatus === 'pending-your-signature'">
                        <b-button
                            v-if="isSignable()"
                            id="signAllDocuments"
                            variant="primary"
                            class="btn btn mb-2"
                            data-test="add-a-new-director"
                            :disabled="isDisabledSignAllBtn"
                            @click="signAll()"
                        >
                            <span class="d-flex align-items-center text-nowrap" style="font-size: 1.155rem; padding: 2px">
                                Sign All Documents
                                <vector class="ml-1" data-name="signature" data-width="18px" data-height="18px" />
                            </span>
                        </b-button>
                    </div>

                    <div v-if="shouldShowDownloadAndUploadButton" class="download-send-email-upload-action-btn">
                        <b-button
                            variant="primary"
                            class="btn btn-sm mb-2 "
                            data-test="download-zip-btn"
                            @click="download()"
                        >
                            <span class="d-flex align-items-center" style="padding:3px;">
                                Download
                                <normal-loader v-if="isDownloadingZip" class="ml-.2"/>
                                <feather-icon v-else icon="DownloadIcon" size="15" style="margin-left:8px"/>
                            </span>
                        </b-button>

                        <b-button
                            variant="primary"
                            class="btn btn-sm mb-2 ml-.5"
                            data-test="send-email"
                            @click="sendEmail()"
                        >
                            <span class="d-flex align-items-center" style="padding:3px;">
                                Email
                                <normal-loader v-if="isSendingMail" class="ml-.2"/>
                                <feather-icon v-else icon="SendIcon" size="13" style="margin-left:8px"/>
                            </span>
                        </b-button>
                    </div>
                </div>

                <div v-if="shouldShowDownloadAndUploadButton">
                    <b-button
                        variant="primary"
                        class="btn btn-sm mb-2 ml-.5"
                        data-test="upload-external-documents"
                        :disabled="isUploadingExternalDocumentProps"
                        @click="requestCloudFiles"
                    >
                        <span v-if="isUploadingExternalDocumentProps" class="d-flex align-items-center" style="padding:3px;">Uploading...</span>
                        <span v-else class="d-flex align-items-center" style="padding:3px;">
                            Upload 
                            <feather-icon icon="UploadIcon" size="15" style="margin-left:8px"/>
                        </span>
                    </b-button>
                    
                    <b-sidebar
                        id="sidebar-task-handler"
                        class="sidebar"
                        sidebar-class="sidebar-lg upload-external-doc-sidebar"
                        :visible="isDocumentSidebarActive"
                        bg-variant="white"
                        shadow
                        backdrop 
                        no-header
                        right
                        @hidden="isDocumentSidebarActive = false"
                    >
                        <template #default="{}">
                            <!-- Body -->
                            <loader v-if="isLoadingExternalDocuments" class="loading-custom" data-type="TableWithFolderSideBar" />

                            <div v-else>
                                <div class="p-1" @click="goBack">
                                    <vector data-name="back-button" data-width="25px" data-height="25px" />
                                </div>

                                <b-table
                                    ref="refFileOrFolderListTable"
                                    class="file-or-folder-table position-relative"
                                    table-class="dont-capitalize-table"
                                    :items="fileOrFolderListData"
                                    :fields="documenTableColumns"
                                    selectable
                                    :sort-by.sync="sortBy"
                                    select-mode="single"
                                    selected-variant="info text-primary"
                                    responsive
                                    primary-key="baseName"
                                    @row-dblclicked="getSelectedRowData"
                                >
                                    <template #cell(fileName)="data">
                                        <span class="company-name">
                                            <div :ref="data.item.baseName" style="padding:7px" @mousedown="activeBasename=data.item.baseName">
                                                <vector v-if="isGDriveFolderType(data.item.type)" ref="folderIcon" data-test="folder-icon" data-name="folder" data-class="mr-1" />
                                                <vector v-if="isGDriveFileType(data.item.type)" :data-should-fill="false" :data-test="mimeToFileName(data.item.mimeType)" :data-name="mimeToFileName(data.item.mimeType)" data-class="mr-1" data-width="17px"/>
                                                <strong style="user-select: none;">{{ data.item.fileName }}</strong>
                                            </div>
                                        </span>
                                    </template>

                                    <template #cell(fileSize) = "data">
                                        <div style="padding:7px; user-select: none;"> {{ data.item.fileSize && ! data.item.fileSize.includes('0') ? data.item.fileSize : '—' }}  </div>
                                    </template>

                                    <template #cell(lastModified) = "data">
                                        <div style="padding:7px 0px; user-select: none;"> {{ data.item.lastModified ? data.item.lastModified : '—' }} </div>
                                    </template>
                                </b-table>
                            </div>

                        </template>
                    </b-sidebar>
                </div>
            </div>
        </div>


    <!-- filter and search -->
        <div class="my-2 filter-and-search">
            <b-row>
                <b-col
                    cols="12"
                    md="6"
                    class="
                        d-flex
                        align-items-center
                        justify-content-start
                        mb-1 mb-md-0
                        show-entries-select-box
                    "
                    >
                    <label class="show">Show</label>
                        <v-select
                            v-model="perPage"
                            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                            :options="perPageOptions"
                            :clearable="false"
                            class="per-page-selector d-inline-block mx-50"
                        />
                    <label>entries</label>
                </b-col>

                <!-- FILTERS -->
                <b-col class="table-search-input" cols="12" md="6">
                    <b-row class="justify-content-end">
                        <div class="d-flex align-items-center justify-content-end">
                            <b-form-input
                                v-model="searchQuery"
                                class="d-inline-block mr-1"
                                placeholder="Search..."
                                data-test="search"
                            />
                        </div>
                    </b-row>
                </b-col>
            </b-row>
        </div>

        <!-- end filter and search -->
        <b-table
            ref="refDocumentListTable"
            class="file-list position-relative"
            table-class="dont-capitalize-table"
            :items="documentList"
            :fields="tableColumns"
            :current-page="currentPage"
            :per-page="perPage"
            responsive
            primary-key="baseName"
        >
            <template #cell(fileName)="data">
                <div class="cursor-pointer text-decoration-underline d-flex align-items-center" :data-test="`view-${data.item.id}`" @click="view(data)">
                    <vector :data-should-fill="false" data-test="pdf" data-name="pdf" data-class="mr-.5" />
                    <span class="document-name" aria-disabled="data.item.isViewRequest"> {{ data.item.fileName}} </span> 
                    <normal-loader v-if="data.item.requestingDocumentSignatureStatusForViewing" class="ml-.2"/>
                    <span v-else style="margin-left:24px;"></span>
                </div>
            </template>

            <template #cell(signDate)="data">
                {{ data.item.signDate }}
            </template>

            <!-- checkbox for send emil selected document -->
            <template #head(selectAll)>
                <b-form-checkbox :checked="isSelectAllChecked" class="select-all" @change="selectAllDocuments($event)"></b-form-checkbox>
            </template>

            <template #cell(selectAll)="data">
                <b-form-group v-slot="{ ariaDescribedby }">
                    <b-form-checkbox-group
                        id="selected-documents"
                        v-model="selectedDocuments"
                        :aria-describedby="ariaDescribedby"
                        name="selected-documents"
                    >
                        <b-form-checkbox :value="data.item.id" :data-test="`checked-${data.item.id}`"></b-form-checkbox>
                    </b-form-checkbox-group>
                    </b-form-group>
            </template>

            <!-- checkbox for send emil selected document -->
            <template #cell(status)="data">
                <div v-if="data.item.processStatus === 'Cancelled'">
                    <b-badge
                        pill
                        variant="light-danger"
                        class="text-capitalize"
                    >
                        {{ (data.item.processStatus) }} 
                    </b-badge>
                </div>

                <div v-else>
                    <b-badge
                        v-if="data.item.status !== 'Signing in progress'"
                        pill
                        :variant="`light-${resolveDocumentStatusVariant(
                            data.item.status
                        )}`"
                        class="text-capitalize"
                    >
                        {{ (data.item.status) }} 
                    </b-badge>
                    <div v-else class="d-flex align-items-center">
                        <span class="text-nowrap"> {{ data.item.status }} </span> 
                        <span class="signing-progress-loader" style="left: 5px"></span>
                    </div>
                </div>
            </template>

            <template #cell(actions)="data">
                <div>
                    <div
                        v-if="isSignable(data.item)"
                        class="d-flex align-items-center justify-content-end sign"
                        :class="data.item.isSigning ? 'cursor-not-allowed signed-disabled' : 'cursor-pointer'"
                        :data-test="`sign-${data.item.id}`"
                        @click="sign(data.item)"
                    >
                        <vector class="ml-1" data-name="signature" data-width="18px" data-height="18px" />
                        <span style="padding-top: 0.1em; padding-left: 0.5em">Sign</span>
                    </div>

                    <div v-else class="d-flex align-items-center justify-content-end cursor-pointer"> - </div>
                </div>
            </template>

            <template #cell(reivew-actions)="data">
                <div>
                    <b-dropdown ref="dropdownParent" size="sm" menu-class="" dropright variant="link" toggle-class="text-decoration-none" no-caret>
                        <template #button-content>
                            <feather-icon class="dot" icon="MoreVerticalIcon"/>
                        </template>

                        <b-dropdown-item
                            :data-test="`delete-${data.item.companyName}`"
                            @click="remove(data)"
                        >
                            <div class="d-flex align-items-end">
                                <feather-icon icon="TrashIcon"  style="position: relative; bottom: 3px;" />
                                <span style="padding-top: .1em; padding-left: .5em">Delete</span>
                            </div>
                        </b-dropdown-item>

                        <b-dropdown-item
                            :data-test="`view-${data.item.companyName}`"
                            @click="view(data)"
                        >
                            <div class="d-flex align-items-end">
                                <feather-icon icon="EyeIcon" style="position: relative; bottom: 3px;"  />
                                <span style="padding-top: .1em; padding-left: .5em">View</span>
                            </div>
                        </b-dropdown-item>

                        <b-dropdown-item
                            v-if="isRevertible(data.item)"
                            @click="revert(data)"
                        >
                            <div class="d-flex align-items-end">
                                <feather-icon icon="RotateCcwIcon" style="position: relative; bottom: 3px;"  />
                                <span style="padding-top: .1em; padding-left: .5em">Reset Signatures</span>
                            </div>
                        </b-dropdown-item>

                        <div v-if="isDocumentCompletedOrSigned(data.item)">
                            <b-dropdown-divider> mark</b-dropdown-divider>

                            <b-dropdown-item
                                :data-test="`dropbox-signed-${data.item.companyName}`"
                                @click="dropboxSigned(data)"
                            >
                                <div class="d-flex align-items-end">
                                    <vector class="" data-name="dropbox-icon" data-width="14px" data-height="14px" style="position:relative; right: 3px;"/>
                                    <span style="padding-top: .1em; padding-left: .2em">Dropbox Signed</span>
                                </div>
                            </b-dropdown-item>

                            <b-dropdown-item
                                :data-test="`mark-as-signed-${data.item.companyName}`"
                                @click="markAsSigned(data)"
                            >
                                <div class="d-flex align-items-end">
                                    <feather-icon icon="BookmarkIcon" width="15px" height="15px" style="position: relative; right: 3px; bottom:1px"  />
                                    <!-- <vector class="" data-name="dropbox-icon" data-width="11px" data-height="11px" style="position:relative; right: 3px;"/> -->
                                    <span style="padding-top: .1em; padding-left: .2em"> Mark As Signed </span>
                                </div>
                            </b-dropdown-item>

                            <b-dropdown-item
                                :data-test="`physically-signed-${data.item.companyName}`"
                                @click="physicallySigned(data)"
                            >
                                <div class="d-flex align-items-end">
                                    <vector class="" data-name="signature" data-width="14px" data-height="14px" style="position:relative; right: 3px;"/>
                                    <span style="padding-top: .1em; padding-left: .2em">Physically Signed</span>
                                </div>
                            </b-dropdown-item>
                        </div>
                    </b-dropdown>
                </div>
            </template>
        </b-table>

        <loader v-if="isLoading" class="loading-custom" data-type="Table" />

        <!-- pagination start -->
        <div class="mx-2 mb-2">
            <b-row>
                <b-col
                    cols="12"
                    sm="6"
                    class="
                        d-flex
                        align-items-center
                        justify-content-center justify-content-sm-start
                    "
                    >
                    <span class="text-muted"
                        >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                        {{ dataMeta.of }} entries</span
                    >
                </b-col>
            <!-- Pagination -->
                <b-col
                    cols="12"
                    sm="6"
                    class="
                        d-flex
                        align-items-center
                        justify-content-center justify-content-sm-end
                    "
                >
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalFileOrFolders"
                        :per-page="perPage"
                        first-number
                        last-number
                        class="mb-0 mt-1 mt-sm-0"
                        prev-class="prev-item"
                        next-class="next-item"
                    >
                        <template #prev-text>
                        <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                        <template #next-text>
                        <feather-icon icon="ChevronRightIcon" size="18" />
                        </template>
                    </b-pagination>
                </b-col>
            </b-row>
        </div>
        <!-- end pagination  -->
    </div>
</template>

<script>
import {
    BTable,
    BRow,
    BCol,
    BPagination,
    BFormInput,
    BFormSelect,
    BButton,
    BFormGroup,
    BFormCheckboxGroup,
    BFormCheckbox,
    BBadge,
    BSidebar,
    BDropdown,
    BDropdownItem,
    BDropdownDivider
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapGetters } from 'vuex';
import { errorToast } from '@/views/helpers/toast';
import { 
    ERROR_DOWNLOAD_MUST_HAVE_AT_LEAST_ONE_DOCUMENTS, 
    ERROR_SEND_EMAIL_MUST_HAVE_AT_LEAST_ONE_DOCUMENTS 
} from "@/constants/errors";
import { DOCUMENT, DRIVE_FILE_TYPE, DRIVE_FOLDER_TYPE } from '@/constants/config';
import Document from '@/models/Document';
import documentLocalizer from '../../documents/view-documents/documentLocalizer';

export default {
    components: {
        BTable,
        BRow,
        BCol,
        BBadge,
        BPagination,
        vSelect,
        BFormInput,
        BFormSelect,
        BButton,
        BFormGroup,
        BFormCheckboxGroup,
        BFormCheckbox,
        BSidebar,
        BDropdown,
        BDropdownItem,
        BDropdownDivider
    },

    props: {
        isLoading: {
            type: Boolean,
            required: true,
        },

        documentTitle : {
            type: String,
            required: true
        },

        isDownloadingZip : {
            type : Boolean,
            default : false
        },

        isSendingMail : {
            type : Boolean,
            default : false
        },

        documentList: {
            type: Array,
            required: true,
        },

        payloadForSignaturesDocumentView : {
            type: Object,
            required: true,
        },

        isSigningAll: {
            type: String,
            default: 'no',
            required: false
        },

        resolveDocumentStatusVariant: {
            type: Function,
            required: false,
            default: () => {}
        },

        // eslint-disable-next-line vue/require-default-prop
        removeCurrentIdFromSelectedIds: {
            type: Array,
            required: false,
        },

        isUploadingExternalDocumentProps: {
            type: Boolean
        }
    },
    
    setup(props) {
        const {
            sortBy,
            isBusy,
            perPage,
            dataMeta,
            dataStatus,
            searchQuery,
            currentPage,
            tableColumns,
            isSortDirDesc,
            totalFileOrFolders,
            perPageOptions,
            refDocumentListTable,

            refetchData,
            filterOn,
        } = props.payloadForSignaturesDocumentView;

        return {
            sortBy,
            isBusy,
            perPage,
            dataMeta,
            dataStatus,
            searchQuery,
            currentPage,
            tableColumns,
            isSortDirDesc,
            totalFileOrFolders,
            perPageOptions,
            refDocumentListTable,

            refetchData,
            filterOn,
        };

    },

    data() {
        return {
            isSelectAllChecked: false,
            activeBasename: "",
            selectedDocuments: [],
            isUploadingExternalDocument: false,
            documentsWithYearOptions: [
                'annual-return', 
                'agm', 
                'agm-dispensation-documents',
                'change-of-fye', 
                'business-activities', 
                'director-fee',
                'director-changes',
                'nominator-changes-documents',
                'update-of-particulars-documents',
                'auditor-changes',
                'secretaries-resignation-documents',
                'data-protection-officers-documents',
                'change-ro',
                'change-of-company-name-documents',
                'interim-dividend',
                'allotment-documents',
                'transfer-documents',
                'share-cancellation-documents',
                'share-buy-back-documents',
                'update-paid-up-capital-documents',
                'sale-of-property'
            ],
            documenTableColumns: [
                { key: 'fileName', label: 'NAME', sortable: true, thClass: 'bg-white text-dark p-2 border-bottom-0', tdClass:'pr-1 border-color-none'},
            ],
            fileOrFolderListData: [ ],
            isLoadingExternalDocuments: false,
            shouldShowModal: false,
            sortBy: 'fileName',
            isDocumentSidebarActive: false,
            selectedFilter: null,
            fullFolderPath: null,

            isDropdown2Visible: false,
        };
    },

    computed: {
        ...mapGetters({
            filterData: 'filterDocuments/getFilterBy',
            filterByOptions: 'filterDocuments/getFilterByOptions'
        }),

        isDisabledSignAllBtn() {
            return this.documentList.every(list => list.isSigning === true);
        },

        shouldShowYearOptions() {
            return this.documentsWithYearOptions.includes(this.$route.name) && this.filterByOptions.length > 0;
        },

        shouldShowDownloadAndUploadButton() {
            return this.dataStatus === 'review-document' && this.documentList.length > 0;
        }
    },

    watch: {
        filterData(filter) {
            if(filter)
                this.selectedFilter = filter.companyProcessId;
        },

        removeCurrentIdFromSelectedIds(ids) {
            if(this.isSelectAllChecked)
                this.isSelectAllChecked = !this.isSelectAllChecked;

            this.selectedDocuments = this.selectedDocuments.filter(selectedId => !ids.includes(selectedId));
        },
        
        selectedDocuments() {
            // console.log(this.selectedDocuments);
        }
    },

    created() {
        if(this.filterData)
            this.selectedFilter = this.filterData.companyProcessId;
    },

    methods: {
        isSignable(document = null)
        {
            if (!document) {
                return this.documentList[0].processStatus !== DOCUMENT.STATUES.CANCELLED;
            }

            if (document && document.processStatus === DOCUMENT.STATUES.CANCELLED) {
                return false;
            }

            if (document) return document.doesItNeedSignature;

            return this.documentList.some(doc => doc.doesItNeedSignature);
        },

        isDocumentCompletedOrSigned(document) {
            if (document.processStatus === DOCUMENT.STATUES.CANCELLED) {
                return false;
            }

            return (
                document && 
                ! (
                    document.status === DOCUMENT.STATUES.COMPLETED || 
                    document.status === DOCUMENT.STATUES.PHYSICALLY_SIGNED ||
                    document.status === DOCUMENT.STATUES.DROPBOX_SIGNED ||
                    document.status === DOCUMENT.STATUES.MARK_AS_SIGNED
                )
            );
        },

        view(document)
        {
            this.$emit('view', document.item);
        },

        sign(document) {
            if(!document.isSigning) this.$emit("sign", document);
        },

        signAll() {
            this.$emit("signAll");
        },

        physicallySigned(document) {
            this.$emit('physicallySigned', document);
        },

        dropboxSigned(document) {
            this.$emit('dropboxSigned', document);
        },

        markAsSigned(document) {
            this.$emit('markAsSigned', document);
        },

        download() {
            if(this.selectedDocuments.length < 1) {
                errorToast(ERROR_DOWNLOAD_MUST_HAVE_AT_LEAST_ONE_DOCUMENTS);
                return;
            }
            this.$emit("download", this.selectedDocuments);
        },

        selectAllDocuments(isSelected) {
            this.isSelectAllChecked = !this.isSelectAllChecked;

            this.selectedDocuments = [];
            if(isSelected) {
                this.documentList.forEach(document => {
                    this.selectedDocuments.push(document.id);
                });
            }
        },

        sendEmail() {
            if(this.selectedDocuments.length < 1) {
                errorToast(ERROR_SEND_EMAIL_MUST_HAVE_AT_LEAST_ONE_DOCUMENTS);
                return;
            }
            this.$emit("sendEmail", this.selectedDocuments);
        },

        handleFilterByOnChange(filter) {
            const selected = this.filterByOptions.filter(filterBy => 
                filterBy.companyProcessId === filter
            )[0];

            if(selected) {
                this.$store.commit('filterDocuments/SET_FILTER_BY', selected);
                this.$emit('filterBy', selected);
            }
        },
        
        openFileMenu() {
            this.$refs.externalDocument.click();
        },
        
        uploadExternalDocument(document) {
            // eslint-disable-next-line prefer-destructuring
            this.$emit('uploadExternalDocument', document);
        },
        
        isGDriveFolderType(type)
        {
            return type === DRIVE_FOLDER_TYPE;
        },

        isGDriveFileType(type)
        {
            return type === DRIVE_FILE_TYPE;
        },
        
        isDirectory(file)
        {
            return file.type === DOCUMENT.FOLDER_TYPE;
        },
        
        async requestCloudFiles(event, basename = null) {
            // this.shouldShowModal = true;
            this.isDocumentSidebarActive = true;
            this.isLoadingExternalDocuments = true;

            this.fileOrFolderListData = [];
            const contents = await Document.getContentsOfFolder(basename, 'pdf', true);
            this.fileOrFolderListData = contents.map(fileData => documentLocalizer(fileData));

            this.isLoadingExternalDocuments = false;
        },

        getSelectedRowData(data) {
            const file = data.files;
            this.fullFolderPath += `/${file.baseName}`; 
            console.log(this.fullFolderPath);
            
            if (! file.baseName)
            {
                this.fileOrFolderListData = [];
                return;
            }

            if (this.isDirectory(file)) {
                this.requestCloudFiles(null, file.baseName);
            } else {
                this.isDocumentSidebarActive = false;
                this.uploadExternalDocument(file);
            }
        },
        
        remove(data) {
            this.$emit('remove', data.item);
        },
        
        goBack() {
            const lastPath = `/${this.fullFolderPath.split('/').pop()}`;
            this.fullFolderPath = this.fullFolderPath.replace(lastPath, '');
            const folderName = this.fullFolderPath.split("/").pop();
            this.requestCloudFiles(null, folderName === 'null' ? null : folderName);
        },

        isRevertible(data) {
            return ! data.isExternalPdf;
        },

        revert(data) {
            this.$emit('revert', data.item);
        }
    },
};
</script>

<style>
.table-responsive {
    overflow-x: inherit;
}

.document-name {
    color: #4267B2 ;
}

.document-list .preloader {
    left: 50%;
}

.document-list .preloader div:before {
    background-color: var(--primary-color);
}

.download-send-email-upload-action-btn .preloader div:before{
    background-color: white;
}

#selected-documents {
  margin-top: 6px;
  vertical-align: middle;
}

#selected-documents .custom-control.custom-checkbox {
  margin: 6px 0px;
  vertical-align: middle;
}

.select-input {
    max-width: 40px;
}

.review-contents {
    vertical-align: middle;
}

.signed-disabled {
  color: #b0aaaa;
}

.signed-disabled {
  color: #b0aaaa;
}

.vue-file-agent.file-input-wrapper {
    border: none !important;
}

.close.remove {
    position: relative;
    top: -5px;
}

.vue-file-agent .file-preview-wrapper::before {
    background: none;
    border: 1px dotted;
}

.b-sidebar.upload-external-doc-sidebar {
  width: 40rem;
}

.sidebar.upload-external-doc-sidebar table, tbody {
    font-size: .9em;
}

/* sign in progress loader */
.signing-progress-loader {
    position: relative;
    width: 45px;
    height: 10px;
    background-repeat: no-repeat;
    background-image:   linear-gradient(#2c74cb 50px, transparent 0),
                        linear-gradient(#2c74cb 50px, transparent 0),
                        linear-gradient(#2c74cb 50px, transparent 0),
                        linear-gradient(#2c74cb 50px, transparent 0),
                        linear-gradient(#2c74cb 50px, transparent 0),
                        linear-gradient(#2c74cb 50px, transparent 0);
    background-position: 0px center, 15px center, 30px center, 45px center, 60px center, 75px center, 90px center;
    animation: rikSpikeRoll 0.65s linear infinite alternate;
}
@keyframes rikSpikeRoll {
  0% { background-size: 10px 3px;}
  16% { background-size: 10px 50px, 10px 3px, 10px 3px, 10px 3px, 10px 3px, 10px 3px}
  33% { background-size: 10px 30px, 10px 50px, 10px 3px, 10px 3px, 10px 3px, 10px 3px}
  50% { background-size: 10px 10px, 10px 30px, 10px 50px, 10px 3px, 10px 3px, 10px 3px}
  66% { background-size: 10px 3px, 10px 10px, 10px 30px, 10px 50px, 10px 3px, 10px 3px}
  83% { background-size: 10px 3px, 10px 3px,  10px 10px, 10px 30px, 10px 50px, 10px 3px}
  100% { background-size: 10px 3px, 10px 3px, 10px 3px,  10px 10px, 10px 30px, 10px 50px}
}
/* end sign in progress loader */  
</style>

<style scoped>

</style>
